import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { Select, inputCls, money, vehLabel } from "@/components/shop/common";

const Draft = ({ d }) => (
  <div className="border border-primary/40 bg-primary/5 rounded-sm p-4 space-y-3 text-sm" data-testid="ai-draft-preview">
    <p><span className="font-mono text-[10px] uppercase tracking-widest text-primary">Cause</span><br />{d.cause}</p>
    <p><span className="font-mono text-[10px] uppercase tracking-widest text-primary">Correction</span><br />{d.correction}</p>
    {d.diagnostic_steps?.length > 0 && (
      <ol className="list-decimal pl-5 space-y-1 text-muted-foreground">{d.diagnostic_steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
    )}
    <div className="grid sm:grid-cols-2 gap-3">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-widest text-primary mb-1">Labor</p>
        {d.labor_lines.map((l) => <p key={l.id} className="text-xs">{l.description} — {l.hours}h</p>)}
      </div>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-widest text-primary mb-1">Parts</p>
        {d.part_lines.map((p) => <p key={p.id} className="text-xs">{p.qty}× {p.description} — {money(p.unit_price)}</p>)}
      </div>
    </div>
  </div>
);

export default function NewWorkOrderDialog({ open, onOpenChange, presetCustomer }) {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [f, setF] = useState({ customer_id: "", vehicle_id: "", complaint: "" });
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open) return;
    setDraft(null);
    setF({ customer_id: presetCustomer || "", vehicle_id: "", complaint: "" });
    api.get("/customers").then((r) => setCustomers(r.data));
  }, [open, presetCustomer]);

  useEffect(() => {
    if (!f.customer_id) return setVehicles([]);
    api.get(`/vehicles?customer_id=${f.customer_id}`).then((r) => {
      setVehicles(r.data);
      if (r.data.length === 1) setF((x) => ({ ...x, vehicle_id: r.data[0].id }));
    });
  }, [f.customer_id]);

  const runDraft = async () => {
    setBusy("draft");
    try { const { data } = await api.post("/ai/draft-work-order", { vehicle_id: f.vehicle_id, complaint: f.complaint }); setDraft(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const create = async () => {
    setBusy("create");
    try {
      const body = { ...f, ...(draft ? { cause: draft.cause, correction: draft.correction, labor_lines: draft.labor_lines, part_lines: draft.part_lines,
        notes: draft.diagnostic_steps?.length ? "Diagnostic steps:\n" + draft.diagnostic_steps.map((s, i) => `${i + 1}. ${s}`).join("\n") : "" } : {}) };
      const { data } = await api.post("/work-orders", body);
      toast.success(`Work order #${data.number} created`);
      onOpenChange(false);
      navigate(`/app/work-orders/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const ready = f.customer_id && f.vehicle_id;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-wo-dialog">
        <DialogHeader><DialogTitle className="font-display text-3xl uppercase">New work order</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <Select value={f.customer_id} onChange={(e) => setF({ ...f, customer_id: e.target.value, vehicle_id: "" })} placeholder="Select customer"
              options={customers.map((c) => ({ value: c.id, label: c.company ? `${c.name} (${c.company})` : c.name }))} data-testid="new-wo-customer-select" />
            <Select value={f.vehicle_id} onChange={(e) => setF({ ...f, vehicle_id: e.target.value })} placeholder={f.customer_id ? "Select vehicle / unit" : "Pick a customer first"}
              options={vehicles.map((v) => ({ value: v.id, label: vehLabel(v) }))} data-testid="new-wo-vehicle-select" />
          </div>
          {customers.length === 0 && <p className="text-xs text-muted-foreground">No customers yet — add one under Customers & units first.</p>}
          {f.customer_id && vehicles.length === 0 && <p className="text-xs text-muted-foreground">This customer has no vehicles — add one under Customers & units.</p>}
          <textarea rows={3} placeholder="Customer complaint, e.g. 'grinding noise when braking, pulls left'" value={f.complaint}
            onChange={(e) => setF({ ...f, complaint: e.target.value })} className={`${inputCls} h-auto py-2`} data-testid="new-wo-complaint-input" />
          <Button variant="outline" onClick={runDraft} disabled={!ready || f.complaint.trim().length < 3 || !!busy}
            className="w-full rounded-sm border-primary/50 text-primary hover:bg-primary/10 hover:text-primary" data-testid="ai-draft-btn">
            {busy === "draft" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            {busy === "draft" ? "AI is writing the work order..." : draft ? "Re-draft with AI" : "Let AI write the work order"}
          </Button>
          {draft && <Draft d={draft} />}
          <Button onClick={create} disabled={!ready || !!busy} className="w-full h-11 rounded-sm font-semibold hover:bg-orange-600 hover:text-white" data-testid="new-wo-create-btn">
            {busy === "create" ? "Creating..." : draft ? "Create work order from AI draft" : "Create work order"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
